import { h } from 'preact';
import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { confirm } from 'utils/modal';
import { canUser } from 'utils/roles';
import { joinClasses } from 'utils/utils';
import { getReleaseMessaging, getDefaultCtaText } from 'components/editor/pages/focus/landing/utils';

import Button from 'components/button/button';
import HomeEditor from 'components/editor/movies/home/home';
import SelectableSection from 'components/editor/selectableSection';
import MediaField from 'components/editor/settings/mediaField';
import SimpleField from 'components/editor/settings/simpleField';
import ToggleField from 'components/editor/settings/toggleField';
import EyeIcon from 'components/icons/eyeIcon';
import EyeIconHover from 'components/icons/eyeIconHover';
import IconPlus from 'components/icons/plus';
import TicketIcon from 'components/icons/ticketIcon';
import Image from 'components/image/image';
import GoToMediaButton from 'components/media/goToMediaButton';

import s from 'components/editor/movies/home/banner/homeBanner.sss';

export default class BannerHomeEditor extends HomeEditor {
	s = s;

	constructor(props) {
		super(props);

		this.onResetHeroClick = this.onResetHeroClick.bind(this);
	}

	renderCtaButton() {
		const movieData = this.movie;
		const heroData = movieData?.pages?.home?.hero || {};

		// Get hero data - prioritize hero still from Media section
		const ctaData = heroData.cta || {};
		const autoCtaText = getDefaultCtaText(movieData);
		// Use auto-generated CTA text only (not the override) for consistency with front-end
		const ctaText = autoCtaText || '';
		// Only show CTA if there's a valid auto CTA AND it's enabled
		const ctaEnabled = ctaData.enabled !== false && !!autoCtaText;
		// Determine icon type based on page status (same logic as CTA text)
		const showtimesPageActive = movieData.pages?.showtimes?.active === true;
		const iconType = showtimesPageActive ? 'ticket' : 'eye';

		if (!ctaEnabled || !ctaText) {
			return null;
		}
		
		return (
			<SelectableSection section="/pages/home/hero" class={this.s.heroLink} {...this.props}>
				<span>{ctaText}</span>
				{iconType === 'eye' ? [
					<EyeIcon class={this.s.iconDefault} key="default" />,
					<EyeIconHover class={this.s.iconHover} key="hover" />
				] : (
					<TicketIcon class={this.s.iconTicket} />
				)}
			</SelectableSection>
		);
	}

	renderBackground() {
		const movieData = this.movie;
		const heroData = movieData?.pages?.home?.hero || {};
		const movieTitle = movieData?.title || 'Movie Title';

		const heroStillUrl = movieData?.hero?.still?.url;
		const heroStillAlt = movieData?.hero?.still?.alt;
		const heroFocalPoint = movieData?.hero?.still?.focalPoint;
		const heroImageUrl = heroStillUrl || heroData.heroImage?.url || '';
		const heroImageAlt = heroStillAlt || heroData.heroImage?.alt || `${movieTitle} Poster`;
		return (
			<SelectableSection section="/pages/home/hero" {...this.props} class={this.s.asset} key="asset">
				{heroImageUrl ? (
					<Image src={heroImageUrl} alt={heroImageAlt} focalPoint={heroFocalPoint} />
				) : (
					<div class={this.s.emptyAsset}>
						<IconPlus /><span>Add hero image</span>
					</div>
				)}
			</SelectableSection>
		);
	}

	renderTitle() {
		const movieData = this.movie;
		const movieTitle = movieData?.title || 'Movie Title';

		// Two-level override system:
		// 1. Movie-specific override (from Hero section editor) - only affects this movie page
		// 2. Global override (from Movie Info page) - checked inside getReleaseMessaging()
		// 3. Auto-generated based on status - fallback inside getReleaseMessaging()
		const releaseMessaging = movieData.pages?.home?.hero?.releaseMessagingOverride || getReleaseMessaging(movieData);

		return (
			<SelectableSection {...this.props} class={this.s.heroTitle} section="/pages/home/hero" key="title">
				<h2>{movieTitle}</h2>
				{releaseMessaging && (
					<div class={this.s.heroStatus}>
						<span>{releaseMessaging}</span>
					</div>
				)}
			</SelectableSection>
		);
	}

	renderContent() {
		const movieData = this.movie;

		const heroData = movieData?.pages?.home?.hero || {};
		const heroStillUrl = movieData?.hero?.still?.url;
		const heroImageUrl = heroStillUrl || heroData.heroImage?.url || '';

		return (
			<div class={joinClasses(this.s.wrapper, !heroImageUrl && this.s.defaultImage, this.props.class)}>
				{/* CTA Button (top right) */}
				{this.renderCtaButton()}

				{/* Hero Background Image */}
				{this.renderBackground()}

				{/* Hero Title (bottom overlay) - uses title treatment or movie title */}
				{this.renderTitle()}
			</div>
		);
	}

	async onResetHeroClick() {
		const confirmed = await confirm('This will reset the hero buttons to the defaults and cannot be undone.', {
			title: 'Reset to defaults?',
			danger: true,
			button: 'Reset to defaults'
		});
		if (!confirmed) return;

		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/pages/home/hero`, {
			cta: { text: '', destination: '', enabled: true },
			releaseMessagingOverride: '',
		});

		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/hero/still`, {
			url: '',
			alt: ''
		});
	}

	renderCtaButtonSettings() {
		const readonly = !canUser('write');
		const ctaData = this.movie.pages?.home?.hero?.cta || {};
		const ctaPath = `${this.props.dataRoot}/content/pages/home/hero/cta`;
		
		// Get auto CTA text to determine if CTA section should be enabled
		const autoCtaText = getDefaultCtaText(this.movie);
		const hasValidCta = !!autoCtaText;
		
		// Auto-populate CTA text if empty and there's a valid CTA
		const ctaTextPath = `${ctaPath}/text`;
		const currentCtaText = jsonPointer.get(Store.get().data, ctaTextPath);
		if (!currentCtaText && autoCtaText) {
			jsonPointer.setDeep(Store.get().data, ctaTextPath, autoCtaText);
		}

		const ctaEnabled = ctaData.enabled !== false;

		return (
			<div class={this.s.settingSection} style={{ opacity: hasValidCta ? 1 : 0.5, pointerEvents: hasValidCta ? 'auto' : 'none' }}>
				{/* <h4>CTA</h4>
					<div class={this.s.item}>
						<span>{ctaText}</span>
						<button class={this.s.edit} onClick={openCtaEditor} disabled={readonly || !hasValidCta}>
							Edit
						</button>
					</div> */}
				<ToggleField
					dark
					checked={ctaEnabled}
					path={`${ctaPath}/enabled`}
					disabled={readonly || !hasValidCta}
				>
					Show CTA
				</ToggleField>
			</div>
		);
	}

	renderReleaseMessagingSettings() {
		const readonly = !canUser('write');
		const autoMessaging = getReleaseMessaging(this.movie);

		return (
			<div class={this.s.settingSection}>
				<SimpleField
					dark
					label="Release messaging"
					path={`${this.props.dataRoot}/content/pages/home/hero/releaseMessagingOverride`}
					placeholder={autoMessaging || 'Release messaging'}
					disabled={readonly}
					help="Override for this movie page only. For global override, use Movie Info page."
				/>
			</div>
		);
	}

	renderHeroSettings() {
		const readonly = !canUser('write');

		return (
			<div class={this.s.settingSection}>
				<MediaField
					dark
					label="Image"
					path={`${this.props.dataRoot}/content/hero/still/url`}
					focalPointPath={`${this.props.dataRoot}/content/hero/still/focalPoint`}
					group={this.props.data?.id}
					assetData={{ type: 'Hero Image', aspectRatio: '16:9', recommendedDimensions: '1920:1080' }}
					disabled={readonly}
				/>
				<SimpleField
					dark
					path={`${this.props.dataRoot}/content/hero/still/alt`}
					label="Hero image alt text"
					placeholder="Thumbnail image for..."
					disabled={readonly}
				/>
				<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
					<Button dark text danger onClick={this.onResetHeroClick} disabled={readonly}>
						Reset hero
					</Button>
				</div>
			</div>
		);
	}

	renderVideosSettings() {
		return (
			<div class={this.s.settingSection}>
				<h4>Videos</h4>
				<span>Videos can be edited from the movie page media tab.</span>
				<GoToMediaButton dark movie={this.movie} />
			</div>
		);
	}

	renderSettings() {
		return (
			<div class={this.s.itemEditor}>
				{/* CTA Button Item */}
				{this.renderCtaButtonSettings()}

				{/* Release Messaging */}
				{this.renderReleaseMessagingSettings()}

				{/* Hero Image */}
				{this.renderHeroSettings()}

				{/* Videos */}
				{this.renderVideosSettings()}
			</div>
		);
	}
}
